/**
 * Internal state shape for the self-schedule hook.
 *
 * We keep three explicit facets per day — trying to collapse into a
 * single `picks: SelfSlot[]` bit us on "user removes a generated
 * slot while auto-fill is on": the merger kept re-adding it. The
 * three-facet model matches legacy, and every reducer moves exactly
 * one of these fields at a time so reasoning stays simple.
 *
 *   `customPicks`        — slots the user drew by hand (origin
 *                          permanently `'custom'`). Never affected
 *                          by pipeline updates.
 *
 *   `droppedIds`         — ids of *generated* slots the user has
 *                          removed via × while `autoFillRequested`.
 *                          The merger skips any generated id that
 *                          appears here, so "remove sticks" across
 *                          re-renders.
 *
 *   `autoFillRequested`  — "fill this day with algorithm output".
 *                          Set by toggling the day checkbox or
 *                          Find Time. Never flipped by a manual
 *                          click — that's the legacy invariant
 *                          that keeps the week checkbox from
 *                          jumping to fully-checked on first click.
 *
 * See `./README.md` for the per-facet ownership table and
 * invariants. The merger in `merge/mergePicks.ts` is the single
 * consumer of this shape.
 */
import type { SelfSlot } from '../types';

export interface DayState {
  /** User-drawn picks only. Origin is always `'custom'`. */
  customPicks: SelfSlot[];

  /**
   * Generated slot ids the user has removed via the × button. When
   * `autoFillRequested=true` the merger still honours these (skips
   * them). Cleared when the day is re-toggled on.
   */
  droppedIds: Set<string>;

  /**
   * `true` means "fill this day with algorithm output". Set by the
   * user clicking the day checkbox (true) or Find Time, or cleared
   * by unchecking. Never touched by manual click.
   */
  autoFillRequested: boolean;
}

export type DayStateMap = Record<string, DayState>;

/** Fresh empty state for a day. */
export const EMPTY_DAY: DayState = {
  customPicks: [],
  droppedIds: new Set(),
  autoFillRequested: false,
};
